import { icons } from 'components/BionicUI/icons/BPIcon';
import { cn } from 'lib/utils';
import { FC } from 'react';
import CardIcon from './CardIcon';

export type SubtitleHighlight = 'green' | 'blue' | undefined;
interface CardTitleProps {
  title: string;
  subtitle?: string;
  subtitleHighlight?: SubtitleHighlight;
  icon?: keyof typeof icons;
  iconImageUrl?: string;
  showIconAnimation?: boolean;
  onIconAnimationComplete?: () => void;
  forceFullSize?: boolean;
  stackLogo?: boolean;
}

const CardTitle: FC<CardTitleProps> = ({
  title,
  subtitle,
  subtitleHighlight,
  icon = undefined,
  iconImageUrl = undefined,
  showIconAnimation = false,
  onIconAnimationComplete = () => {},
  forceFullSize = false,
  stackLogo = false,
}) => (
  <div
    className={cn(
      'grid relative justify-start items-center leading-none',
      'gap-x-3 @md:gap-x-4 @lg:gap-x-5',
      forceFullSize ? 'gap-x-5' : '',
      stackLogo ? 'grid-rows-[auto_1fr] gap-4 -mt-1' : 'grid-cols-[auto_1fr]'
    )}
  >
    {(icon || iconImageUrl) && (
      <div className="grid relative w-full h-full items-start">
        <CardIcon
          icon={icon}
          iconImageUrl={iconImageUrl}
          animate={showIconAnimation}
          onIconAnimationComplete={onIconAnimationComplete}
          forceFullSize={forceFullSize}
        />
      </div>
    )}
    <div
      className={cn(
        'grid relative items-center justify-start grid-flow-row gap-[6px] @sm:gap-[7px]'
      )}
    >
      <Title title={title} forceFullSize={forceFullSize} />
      {subtitle && (
        <Subtitle
          subtitle={subtitle}
          subtitleHighlight={subtitleHighlight}
          forceFullSize={forceFullSize}
        />
      )}
    </div>
  </div>
);

const Title: FC<{ title: string; forceFullSize: boolean }> = ({
  title,
  forceFullSize,
}) => (
  <div
    className={cn(
      'block relative font-semiBold',
      forceFullSize
        ? 'text-[24px]'
        : 'text-[18px] @sm:text-[20px] @md:text-[22px] @lg:text-[24px]'
    )}
  >
    {title}
  </div>
);

const SubtitleBlob = () => (
  <div className="inline-block relative w-[8px] h-[8px] mr-2 mb-[2px] rounded-full bg-bionic-green animate-pulse" />
);

const Subtitle: FC<{
  subtitle: string;
  subtitleHighlight: SubtitleHighlight;
  forceFullSize: boolean;
}> = ({ subtitle, subtitleHighlight, forceFullSize }) => (
  <div
    className={cn(
      'block relative',
      forceFullSize ? 'text-[16px]' : 'text-[14px] @md:text-[16px]',
      subtitleHighlight === 'green' ? 'text-bionic-green' : 'text-bionic-muted'
    )}
  >
    {subtitleHighlight ? <SubtitleBlob /> : ''}
    {subtitle}
  </div>
);

export default CardTitle;
